/**
 * Hub Member auth state.
 *
 * Reads /api/auth/google/me on mount and exposes:
 *   { ready, member, refresh, logout, setPhone }
 *
 * member = null → not signed in
 * member.phone_verified = false → signed in but no phone yet → phone capture UI
 * member.phone_verified = true → fully onboarded → can book / buy membership
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";

const HubMemberContext = createContext({
  ready: false, member: null, refresh: async () => {}, logout: async () => {}, setPhone: async () => {},
});

const API = process.env.REACT_APP_BACKEND_URL;

export function HubMemberProvider({ children }) {
  const [ready, setReady] = useState(false);
  const [member, setMember] = useState(null);

  const refresh = useCallback(async () => {
    // Always try /me. On a fresh return from OAuth the cookie may not be set
    // yet, but that just yields member=null — GoogleAuthCallback will call
    // refresh() again after it exchanges the session_id. Don't gate on the
    // hash here, or the app gets stuck on "Loading…" until the user refreshes.
    try {
      const r = await axios.get(`${API}/api/auth/google/me`, { withCredentials: true });
      setMember(r.data);
    } catch (e) {
      setMember(null);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const logout = useCallback(async () => {
    try { await axios.post(`${API}/api/auth/google/logout`, {}, { withCredentials: true }); }
    catch (e) { /* ignore */ }
    setMember(null);
  }, []);

  const setPhone = useCallback(async (phone, marketing_opt_in = true) => {
    const r = await axios.post(
      `${API}/api/auth/google/set-phone`,
      { phone, marketing_opt_in },
      { withCredentials: true },
    );
    await refresh();
    return r.data;
  }, [refresh]);

  return (
    <HubMemberContext.Provider value={{ ready, member, refresh, logout, setPhone }}>
      {children}
    </HubMemberContext.Provider>
  );
}

export function useHubMember() {
  return useContext(HubMemberContext);
}
